<script setup lang="ts">
import config from "@/config";
import { useUserSession } from "@/store/userSession";
import axios from "axios";
import dayjs from "dayjs";
import { useToast } from "primevue/usetoast";
import { useRouter, useRoute } from "vue-router";
import { employees } from "@/api";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { punchOutSession } from "@/store/punchInOut";
import { pushNotificationService } from "@/services/pushNotificationService";
import posthog from "posthog-js";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { initializeApp } from "firebase/app";

dayjs.extend(utc);
dayjs.extend(timezone);

const { onMenuToggle, toggleDarkMode, isDarkTheme } = useLayout();
const authStore = useUserSession();
const menu = ref();
const isCheckedIn = ref(false);
const isLoading = ref(false);
const route = useRoute();
const router = useRouter();
const openConfirmModel = ref(false);
const punchesOfADay: any = ref({});
const api = useAxios();
const op = ref();
const toast = useToast();
const employe = employees(api);
const punchesArr = ref([]);
const notificationArr: any = ref([]);
const notificationCount = ref(0);
const notificationPopover = ref();
const punch = punchOutSession();
const googlegeocodingApiKey = import.meta.env.VITE_GOOGLE_GEOCODING_API_KEY;
const imgUrl = import.meta.env.VITE_API_URL;
const options = {
  enableHighAccuracy: true,
  maximumAge: 0, // Always get fresh location, no caching
  timeout: 30000, // Increased timeout for better accuracy
};
// const URL = `ws://192.168.0.8:8080/ws`;
const wsURL = new URL(import.meta.env.VITE_API_BASE_URL).host;
const wsPrefix = import.meta.env.VITE_WEBSOCKET_PREFIX;
const socket = new WebSocket(`${wsPrefix}${wsURL}/websocket`);
const { counts, getCounts } = useCommon();

const firebaseConfig = {
  apiKey: "AIzaSyAhw-d7OsJ5x-OLrsiaA-1u6EQysR4R6yw",
  authDomain: "ashray-hrms.firebaseapp.com",
  projectId: "ashray-hrms",
  storageBucket: "ashray-hrms.firebasestorage.app",
  messagingSenderId: "27067222009",
  appId: "1:27067222009:web:c32cc17cc5aed1584a9f28",
  measurementId: "G-P6PYHR6WG0"
};

const app = initializeApp(firebaseConfig);
const messaging = getMessaging();


// Connection opened
socket.addEventListener("open", async() => {
  socket.send(
    JSON.stringify({
      eventName: "get-notification-data",
      data: {
        user_id: authStore.user.id,
        employee_id: authStore.user.employee_id,
        token: authStore.token,
      },
    })
  );
  // Listen for messages
  socket.onmessage = async function (event) {
    const eventData = JSON.parse(event.data);

    if (eventData.eventName === "unauthorized") {
      authStore.logoutUser();
      router.push({ name: "login" });
    }
    if (eventData.eventName === "get-notification") {
      notificationArr.value = eventData.data.notifications
        ? eventData.data.notifications
        : [];
      const notificationCountVal = notificationArr.value.length;
      notificationCount.value = notificationCountVal;
      if(window.Notification.permission === "granted") {
        if ("setAppBadge" in window.navigator) {
          const nav: any  = window.navigator;
          if(notificationCountVal > 0) {
            nav.setAppBadge(notificationCountVal).catch((error: Error) => {
              alert("Badging failed:" +  error.message);
              console.error("Badging failed:", error);
            });
          } else {
            nav.clearAppBadge();
          }
        } else {
          // alert("Badging API not available");
          console.error("Badging API not available");
        }
      } else {
        await window.Notification.requestPermission();
      }
    }
  };
});

posthog.identify(
  authStore.user.id,  // Replace 'distinct_id' with your user's unique identifier
  { email: authStore.user.email, name: authStore.user.name } // optional: set additional person properties
);

// ANCHOR - This function is called when the user clicks the "Check In/Out" button for collect location data
async function success(position: any) {
  // Check location accuracy and age
  const accuracy = position.coords.accuracy;
  const timestamp = position.timestamp;
  const locationAge = Date.now() - timestamp;
  
  console.log('Location data:', {
    accuracy: `${accuracy}m`,
    age: `${locationAge}ms`,
    latitude: position.coords.latitude,
    longitude: position.coords.longitude
  });

  // Validate location accuracy (reject if accuracy is worse than 100 meters)
  if (accuracy > 100) {
    toast.add({
      ...config.toastConfig,
      severity: "warn",
      summary: "Low Accuracy",
      detail: `Location accuracy is ${Math.round(accuracy)}m. Please try again for better accuracy.`,
    });
    isLoading.value = false;
    closeConfirmModel();
    return;
  }

  // Warn if location is older than 10 seconds
  if (locationAge > 10000) {
    console.warn(`Location data is ${locationAge}ms old`);
  }

  let latitude, longitude;
  latitude = position.coords.latitude;
  longitude = position.coords.longitude;

  const httpClient = await axios.get(
    "https://maps.googleapis.com/maps/api/geocode/json",
    {
      params: {
        latlng: `${latitude}, ${longitude}`,
        // key: "AIzaSyAEecM3rZgUu_Ao2ZvJaIvO0AT-Yz2hM3c",
        key: googlegeocodingApiKey,
      },
    }
  );

  if (httpClient.status === 200) {
    if (httpClient.data.results.length === 0) {
      
      toast.add({
        ...config.toastConfig,
        severity: "error",
        summary: "Error",
        detail: httpClient.data.error_message || "No location found",
      });
      return;
    }

    // Extract the first formatted_address from the response
    const firstFormattedAddress = httpClient.data.results[0].formatted_address;
    
    const payload = {
      address: firstFormattedAddress,
      location: { long: longitude, lat: latitude },
    };

    const puncRes: any = await employe.employeePunch(payload);

    if (puncRes.status === config.status.success) {
      await getUserPunches();
      await getCounts();
      toast.add({
        ...config.toastConfig,
        severity: "success",
        summary: "Success",
        detail: puncRes.message,
      });
      punch.setPunch(true);
    }
  }

  isLoading.value = false;
  closeConfirmModel();
}

// debugging code main app file
// navigator.serviceWorker.getRegistrations().then(registrations => {
//   console.log('Active service worker registrations:', registrations);
// });

// For Push Notifications
let pushInitialized = false;
// First, detect the browser
async function detectBrowser() {
  const userAgent = navigator.userAgent;
  // console.log("User Agent:", userAgent);
  
  if (userAgent.indexOf("Firefox") > -1) {
    console.log("Browser detected: Firefox");
    return "firefox";
  } else if (userAgent.indexOf("Brave") > -1 || 
            ('brave' in navigator && await (navigator as any).brave.isBrave())) {
    console.log("Browser detected: Brave");
    return "brave";
  } else if (userAgent.indexOf("Chrome") > -1) {
    console.log("Browser detected: Chrome");
    return "chrome";
  } else if (userAgent.indexOf("Safari") > -1) {
    console.log("Browser detected: Safari");
    return "safari";
  } else if (userAgent.indexOf("Edge") > -1) {
    console.log("Browser detected: Edge");
    return "edge";
  } else {
    console.log("Browser detected: Unknown");
    return "unknown";
  }
}

// push notification initialization
async function initializePushNotifications() {
  const browser = await detectBrowser();
  
  // Check if push is supported in this browser
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.warn(`Push notifications are not supported in ${browser}`);
    pushInitialized = false;
    return false;
  }
  
  // Browser-specific handling
  switch(browser) {
    case "firefox":
      // Firefox-specific code
      console.log("Firefox detected, using Firefox-specific push notification setup");
      break;
      
    case "brave":
      // Brave-specific code
      console.log("Brave detected, checking Brave-specific settings");
      break;
      
    case "chrome":
      // Chrome-specific code
      console.log("Chrome detected, using standard push notification setup");
      break;
      
    default:
      console.log(`${browser} detected, using standard push notification setup`);
  }
  
  // Continue with the regular initialization
  const result = await pushNotificationService.initialize();
  
  if (result) {
    console.log(`Push notifications initialized successfully in ${browser}`);

    // Set up periodic subscription check (every 24 hours)
    // setInterval(() => {pushNotificationService.checkAndRenewSubscription();},24 * 60 * 60 * 1000);
    setInterval(() => {
      // console.log(1111)
      pushNotificationService.initialize();
      pushNotificationService.checkAndRenewSubscription();
    },60 * 1000);
    return true;
  } else {
    console.log(`Push notifications not enabled in ${browser}`);
    // Reset flag if initialization failed
    pushInitialized = false;
    
    // Show browser-specific guidance if needed
    if (browser === "firefox") {
      // You could show Firefox-specific instructions
      console.log("For Firefox users: You may need to enable notifications in your privacy settings");
    } else if (browser === "brave") {
      // You could show Brave-specific instructions
      console.log("For Brave users: You may need to disable shields or adjust notification settings");
    }
    return false;
  }
}

onMessage(messaging, (payload) => {
  try {
    // const { notification, data } = payload
    // if(!notification || !notification?.title || !notification?.body) {
    //   return
    // }
    console.log('Message received. ', payload);
    // const notificationData = {
    //   title: 'Ashray HRMS',
    //   body: 'You have a new notification',
    //   icon: '/img/icons/android-chrome-192x192.png',
    //   badge: '/img/icons/android-chrome-192x192.png',
    //   data: {
    //     url: '/',
    //     taskId: '',
    //     notificationId: data ? data['notificationId'] : ''
    //   },
    // };
    // if(data?.type === 'task') {
    //   notificationData['data']['url'] = '/tasks'
    //   notificationData['data']['taskId'] = data['taskId']
    // } else if(data?.type === 'leave') {
    //   notificationData['data']['url'] = '/approve-leave-list'
    // }
    // new Notification(notification.title, {
    //   body: notification.body,
    //   icon: notificationData.icon,
    //   badge: notificationData.badge,
    //   data: notificationData.data,
    // }).addEventListener('click',function(event: Event) {
    //   console.log("event====",event)
    //   if(!event.target) return;
    //   event.preventDefault();
    //   const { data } :any = event.target
    //   setNotificationRead(event, data.notificationId);
    //   if(data.url.indexOf('tasks') > -1) {
    //     authStore.setSelectedTaskId(data.taskId)
    //     router.push(data.url);
    //     if(route.name === "tasks") window.location.reload()
    //   } else if(data.url.indexOf('leave') > -1) {
    //     router.push(data.url);
    //   }
      
    //   event.target.addEventListener('click',function(e: Event) {
    //     console.log("e==========",e)
    //   })
    // });
    navigator.serviceWorker.ready.then((registration) => {
      registration.active?.postMessage({
        type: 'SHOW_NOTIFICATION',
        payload,
      });
    });
  } catch (error) {
    console.log("error===",error)
  }
});

navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
  if(!event.data) return
  const { type, taskId, notificationId } = event.data || {};
  if (type === 'SET_TASK_ID') {
    if (taskId) {
      authStore.setSelectedTaskId(taskId);
    }
    if (notificationId) {
      setNotificationRead(event, notificationId);
    }
  }
});


async function getFirebaseNotifications() {
  const permission = await window.Notification.requestPermission()
  if (permission === 'granted') {
    console.log('Notification permission granted.');
    // generating Token
    generateToken().then(async(fcmToken) => {
      const browser = await detectBrowser();
      const payload = {
        employee_id: authStore.user.employee_id,
        device_token: fcmToken,
        device_type: browser
      }
      if(fcmToken) {
        const response: any = await employe.saveFirebaseSubscription(payload);
        if(response.status === config.status.success) {
          authStore.setUserDeviceToken(fcmToken);          
        } else {
          // getFirebaseNotifications();
        }
      }
    });
  } else {
    await window.Notification.requestPermission();
    getFirebaseNotifications();
  }
}

// generate Firebase Token
async function generateToken() {
  return await getToken(messaging, { vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY });
}

async function getUserPunches() {
  const fromdate = dayjs().format("YYYY-MM-DD");
  const todate = dayjs().format("YYYY-MM-DD");
  const payload = { employee_id: authStore.user.employee_id, fromdate, todate };
  const allPunchesRes: any = await employe.getemployeePunch(payload);
  if (
    allPunchesRes.status === config.status.success &&
    allPunchesRes.data.report
  ) {
    punchesOfADay.value = allPunchesRes.data.report[0];
    const punches = allPunchesRes.data.report[0].punches;
    isCheckedIn.value = punches[punches.length - 1].mode !== "OUT";
    punchesArr.value = punches;
  }
}

function error(err: GeolocationPositionError) {
  closeConfirmModel();
  isLoading.value = false;
  
  let errorMessage = "Location access failed.";
  
  switch (err.code) {
    case err.PERMISSION_DENIED:
      errorMessage = "Please allow location permissions and try again.";
      break;
    case err.POSITION_UNAVAILABLE:
      errorMessage = "Location information is unavailable. Please check your GPS settings.";
      break;
    case err.TIMEOUT:
      errorMessage = "Location request timed out. Please try again.";
      break;
    default:
      errorMessage = "An unknown error occurred while fetching location.";
      break;
  }
  
  toast.add({
    ...config.toastConfig,
    severity: "error",
    summary: "Location Error",
    detail: errorMessage,
  });
}

const items = ref([
  {
    label: "Logout",
    icon: "pi pi-fw pi-sign-out",
    command: () => {
      authStore.logoutUser();
      router.push({ name: "login" });
    },
  },
]);

function toggle(event: any) {
  menu.value.toggle(event);
}

function confirmCheckInOut() {
  openConfirmModel.value = true;
}

function closeConfirmModel() {
  openConfirmModel.value = false;
}

function checkInOut() {
  isLoading.value = true;
  navigator.geolocation.getCurrentPosition(success, error, options);
}

const getPunchTime = computed(() => {
  return punchesOfADay.value?.punches?.length > 0
    ? dayjs(
      punchesOfADay.value.punches[punchesOfADay.value.punches.length - 1]
        .punch_time
    )
      .utc()
      .format("hh:mm A")
    : "-";
});

const getLastPunchMode = computed(() => {
  return punchesOfADay.value?.punches?.length > 0
    ? punchesOfADay.value.punches[punchesOfADay.value.punches.length - 1].mode
    : "-";
});

onMounted(() => {
  getUserPunches();
  // initializePushNotifications();
  getFirebaseNotifications();
});

async function logout() {
  if(authStore.userDeviceToken) {
    const browser = await detectBrowser();
    const payload = {
      employee_id: authStore.user.employee_id,
      device_token: authStore.userDeviceToken,
      device_type: browser
    }
    const response: any = await employe.removeFirebaseSubscription(payload);
    authStore.resetUserDeviceToken();
  }
  authStore.logoutUser();
  router.push({ name: "login" });
}

// async function logout() {
//   try {
//     // Store the current token before clearing
//     const currentToken = authStore.token;
    
//     if (currentToken && "serviceWorker" in navigator && "PushManager" in window) {
//       const registration = await navigator.serviceWorker.ready;
//       const subscription = await registration.pushManager.getSubscription();
      
//       if (subscription) {
//         try {
//           // Make the API call with the current token
//           const apiUrl = import.meta.env.VITE_API_BASE_URL || "";
//           await axios.post(`${apiUrl}/cancel-subscription`,subscription.toJSON(),
//             {
//               headers: {
//                 Authorization: `Bearer ${currentToken}`,
//                 "Content-Type": "application/json",
//               },
//             }
//           );
          
//           // Only unsubscribe locally after successful API call
//           await subscription.unsubscribe();
//           console.log("Successfully unsubscribed from push notifications");
//         } catch (subscriptionError) {
//           console.error("Error cancelling push subscription:", subscriptionError);
//           // If the API call fails, still try to unsubscribe locally
//           try {
//             await subscription.unsubscribe();
//             console.log("Unsubscribed locally after API error");
//           } catch (localError) {
//             console.error("Failed to unsubscribe locally:", localError);
//           }
//         }
//       }
//     }
    
//     // Now logout the user
//     authStore.logoutUser();
//     router.push({ name: "login" });
//   } catch (error) {
//     console.error("Error during logout:", error);
//     // Still attempt to logout even if there was an error
//     authStore.logoutUser();
//     router.push({ name: "login" });
//   }
//   authStore.logoutUser();
//   router.push({ name: "login" });
// }

function togglePopover(event: Event) {
  op.value.toggle(event);
}

function haversine(lat1: any, lon1: any, lat2: any, lon2: any) {
  const R = 6371; // Earth's radius in kilometers
  const toRad = (angle: any) => (angle * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in kilometers
}

function isGenericAddress(address: string): boolean {
  const parts = address
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);

  const uniqueParts = [...new Set(parts)];

  // Count how many parts are numeric (e.g., postal codes)
  const numericPartsCount = parts.filter((p) => /^\d+$/.test(p)).length;

  // Generic if:
  // - Less than 3 unique parts
  // - OR more than half the parts are numeric
  // - OR all parts are repeated (low entropy)
  const isLowEntropy = uniqueParts.length <= 2;
  const isNumericHeavy = numericPartsCount / parts.length > 0.5;

  return isLowEntropy || isNumericHeavy;
}

async function setNotificationRead(event: Event, notification_id: string) {
  try {
    event.preventDefault();
    const res: any =
      await employe.markReadEmployeeNotifications(notification_id);
    if (res.status === config.status.success) {
      notificationPopover.value.hide(event);
    } else {
      toast.add({ ...config.toastConfig, detail: res.message });
    }
  } catch (error) {
    toast.add({ ...config.toastConfig, detail: config.catchErrorMessage });
  }
}

async function handleClickNotificaiton(event: Event, notification: any) {
  try {
    event.preventDefault();
    setNotificationRead(event, notification.id);
    if (notification.notification_type === "leave") {
      notificationPopover.value.hide(event);
      router.push({ name: "approve-leave-list" });
    } else if (notification.notification_type === "task") {
      if (notification.message.includes("removed")) {
        return;
      }
      authStore.setSelectedTaskId(notification.task_id);
      if(route.name === "tasks") {
        return;
      }
      router.push({ name: "tasks" });
    }
  } catch (error) {
    toast.add({ ...config.toastConfig, detail: config.catchErrorMessage });
  }
}

const toggleNotification = (event: Event) => {
  notificationPopover.value.toggle(event);
};

function getInitials(name: string) {
  if (name) {
    return name[0]?.toUpperCase();
  } else {
    return "H"; // Indicated 'W' from Water Taxi
  }
}

function handleViewAll(event: Event) {
  notificationPopover.value.hide(event);
  router.push({ name: "notifications" });
}
</script>

<template>
  <div class="layout-topbar">
    <div class="layout-topbar-logo-container">
      <button class="layout-menu-button layout-topbar-action" @click="onMenuToggle">
        <i class="pi pi-bars" />
      </button>
      <RouterLink to="/" class="layout-topbar-logo !text-base">
        <div class="font-semibold text-xl w-full">Ashray <span>HRMS</span></div>
      </RouterLink>
    </div>

    <div class="layout-topbar-actions">
      <div class="flex items-center cursor-pointer" @click="toggleNotification">
        <div class="relative">
          <i class="pi pi-bell" style="font-size: 1.5rem" />
          <div class="absolute -top-2 -right-2 bottom-0" v-if="notificationCount > 0">
            <Badge :value="notificationCount" severity="danger" size="small"></Badge>
          </div>
        </div>
      </div>
      <div class="layout-topbar-action !w-full flex flex-col !cursor-auto hover:!bg-transparent">
        <div class="text-xs">
          <strong>{{ `Last ${getLastPunchMode.toLowerCase()} time` }}</strong>
        </div>
        <div class="text-xs">
          {{ getPunchTime }}
        </div>
      </div>
      <div v-if="authStore.user.permissions.includes('punch-in-out')" class="layout-topbar-action">
        <ToggleSwitch v-model="isCheckedIn" :ariaLabel="isCheckedIn ? 'OUT' : 'IN'"
          @click.prevent="confirmCheckInOut" />
      </div>
      <div class="layout-topbar-action">
        <button v-styleclass="{
          selector: '@next',
          enterFromClass: 'hidden',
          enterActiveClass: 'animate-scalein',
          leaveToClass: 'hidden',
          leaveActiveClass: 'animate-fadeout',
          hideOnOutsideClick: true,
        }" class="layout-topbar-menu-button layout-topbar-action" @click="togglePopover">
          <i class="pi pi-user" />
        </button>
        <div class="layout-config-menu">
          <div class="layout-topbar-menu-content">
            <Popover ref="op">
              <div class="flex flex-col gap-4 w-full" style="width: 240px">
                <div>
                  <ul class="list-none p-0 m-0 flex flex-col">
                    <li class="flex items-center gap-2">
                      <div>
                        <p class="font-medium truncate">
                          {{ authStore.user.role }}
                        </p>
                        <p class="text-sm text-surface-500 dark:text-surface-400 truncate w-64">
                          {{ authStore.user.email }}
                        </p>
                        <p class="text-sm text-surface-500 dark:text-surface-400 truncate">
                          {{ authStore.user.name }}
                        </p>
                      </div>
                    </li>
                    <li @click="toggleDarkMode" class="cursor-pointer hover:bg-emphasis rounded p-3">
                      <span class="text-lg">
                        <button type="button" class="layout-topbar-action">
                          <i class="pi" :class="[
                            {
                              'pi-moon': isDarkTheme,
                              'pi-sun': !isDarkTheme,
                            },
                          ]" />
                          <span class="ml-2 text-sm">{{
                            isDarkTheme ? "Light Mode" : "Dark Mode"
                            }}</span>
                        </button>
                      </span>
                    </li>
                    <li @click="logout" class="cursor-pointer hover:bg-emphasis rounded p-3">
                      <span class="text-lg">
                        <button type="button" class="layout-topbar-action">
                          <i class="pi pi-fw pi-sign-out" />
                          <span class="ml-2 text-sm">Sign Out</span>
                        </button>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Popover ref="notificationPopover">
    <div class="flex flex-col gap-3 w-[18rem] sm:w-[25rem] md:w-[28rem] cursor-pointer">
      <div>
        <span class="font-semibold block mb-2">Notifications</span>
        <hr />
      </div>
      <div v-if="notificationArr.length > 0">
        <div v-for="(item, index) in notificationArr.slice(0, 4)" :key="index">
          <div class="flex items-center gap-3" @click="handleClickNotificaiton($event, item)">
            <Avatar v-if="item.assigned_by_user_photo" :image="imgUrl + '/' + item.assigned_by_user_photo"
              class="mr-2 cursor-pointer w-[15%]" size="normal" shape="circle"
              style="background-color: #ece9fc; color: #2a1261" />
            <Avatar v-else :label="getInitials(item.assigned_by_user_name)" class="mr-2 cursor-pointer w-[15%]"
              size="normal" shape="circle" style="background-color: #ece9fc; color: #2a1261" />
            <div class="flex w-[85%]" :class="{'flex-row gap-2': item.notification_type === 'leave', 'flex-col': item.notification_type === 'task'}">
              <div class="flex items-center gap-3">
                <div class="font-medium" :class="{
                  ' w-[66%] truncate': item.notification_type == 'task',
                }" :title="item.message">
                  {{ item.message }}
                </div>
                <div class="text-right text-xs">
                  {{ dayjs(item.created_date).format("DD/MM/YYYY hh:mm a") }}
                </div>
              </div>
              <div class="flex items-center gap-3">
                <div class="font-medium w-[95%] truncate" v-if="item.notification_type !== 'leave'">
                  {{
                    !item.message.includes("removed")
                      ? `${item.assigned_by_user_name} has assigned task to you`
                      : `${item.assigned_by_user_name} has removed you from Task`
                  }}
                </div>
                <div class="bg-[#f97316] h-[10px] w-[10px] rounded-full self-center" />
              </div>
            </div>
          </div>
          <hr class="my-2" v-if="index !== notificationArr.length - 1" />
        </div>
      </div>
      <div v-else class="text-center text-gray-500 py-4">
        No new notifications
      </div>
      <div class="text-center">
        <span @click="handleViewAll" class="text-blue-700 underline">View All</span>
      </div>
    </div>
  </Popover>

  <Dialog v-model:visible="openConfirmModel" :style="{ width: '450px' }" header="Confirm" :modal="true"
    :closable="false" @after-hide="isLoading = false">
    <div class="flex items-center gap-4">
      <i class="pi pi-exclamation-triangle !text-3xl" />
      <span>{{
        getLastPunchMode == "IN"
          ? "Do you want to check out?"
          : "Do you want to check in?"
      }}</span>
    </div>
    <template #footer>
      <Button label="No" icon="pi pi-times" severity="danger" text @click="closeConfirmModel" />
      <Button label="Yes" icon="pi pi-check" text @click="checkInOut" :loading="isLoading" />
    </template>
  </Dialog>
</template>
